import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'I18n';
import { MicroDateRange } from 'foundations-components/transitional/components/MicroDateRange';
import { toMoment } from 'foundations-components/transitional/utils/SimpleDate';
import { formatDateForApi, toSimpleDate } from '../../utils/dateRangeUtils';
import { isTimePeriodCustom } from '../../utils/dateUtils';
import useFetchPreferredCurrency from '../multicurrency/hooks/useFetchPreferredCurrency';
import { getCurrentDate } from '../../selectors/dateSelectors';
import { CUSTOM_DATE_FORMAT } from '../../constants/DateConstants';
import { jsx as _jsx } from "react/jsx-runtime";
export const TimePeriodDateRange = ({
  onDateSelect,
  timePeriodFilterValue
}) => {
  const currentDate = useSelector(getCurrentDate);
  const {
    userHasDifferentPreferredCurrency,
    loading,
    error
  } = useFetchPreferredCurrency();
  const isCurrentFilterCustom = isTimePeriodCustom(timePeriodFilterValue);

  // Notes on "(!isCurrentFilterCustom || undefined)":
  // - This seems unintuitive, but is intentional: we should only show the calendar component
  //   when the user has clicked "Custom Date" but has not selected a start / end date yet
  // - We use `undefined` instead of `false` to allow MicroDateRange to use its default open behavior
  //   (will open calendar on click)
  const [isOpen, setIsOpen] = useState(!isCurrentFilterCustom || undefined);
  const initialStartDate = isCurrentFilterCustom ? timePeriodFilterValue.get('startDate') : null;
  const initialEndDate = isCurrentFilterCustom ? timePeriodFilterValue.get('endDate') : null;

  // We set to undefined as opposed to null because that's the type `MicroDateRange` expects if start/end date is not provided
  const formattedStartDate = toSimpleDate(`${initialStartDate}`) || undefined;
  const formattedEndDate = toSimpleDate(`${initialEndDate}`) || undefined;
  const [startDate, setStartDate] = useState(formattedStartDate);
  const [endDate, setEndDate] = useState(formattedEndDate);
  const isRestrictedToWithinTwoYears = !loading && !error && userHasDifferentPreferredCurrency;
  const twoYearsAgoSimpleDate = toSimpleDate(I18n.moment(currentDate).add(1, 'day').subtract(2, 'year').format(CUSTOM_DATE_FORMAT)) || undefined;
  const handleChange = useCallback(event => {
    var _event$target, _event$target2;
    const eventStartDate = event === null || event === void 0 || (_event$target = event.target) === null || _event$target === void 0 || (_event$target = _event$target.value) === null || _event$target === void 0 ? void 0 : _event$target.startDate;
    const eventEndDate = event === null || event === void 0 || (_event$target2 = event.target) === null || _event$target2 === void 0 || (_event$target2 = _event$target2.value) === null || _event$target2 === void 0 ? void 0 : _event$target2.endDate;

    // Case: There is no start or end date (user has clicked Clear button)
    if (!eventStartDate && !eventEndDate) {
      setStartDate(undefined);
      setEndDate(undefined);
      return;
    }

    // Set the dates in state based on the event values
    setStartDate(eventStartDate);
    setEndDate(eventEndDate);

    // Case: We have a saved start date and are now setting the end date
    if (eventStartDate && eventEndDate && toMoment(eventStartDate).isValid() && toMoment(eventEndDate).isValid() && toMoment(eventEndDate).isAfter(toMoment(eventStartDate))) {
      setIsOpen(undefined);
      onDateSelect({
        startDate: formatDateForApi(eventStartDate),
        endDate: formatDateForApi(eventEndDate)
      });
    }
  }, [onDateSelect]);
  return /*#__PURE__*/_jsx(MicroDateRange, {
    "data-test-id": "custom-date-range-input",
    inputRef: null,
    value: {
      startDate,
      endDate
    },
    min: isRestrictedToWithinTwoYears ? twoYearsAgoSimpleDate : undefined,
    minValidationMessage: I18n.text('repOverviewTable.filters.timePeriod.custom.restricted'),
    onChange: handleChange,
    open: isOpen,
    showPresets: false
  });
};